import { API, Card } from '@enkla-gui'
import React, { useEffect, useState } from 'react'

import ReportExport from '../ReportExport'
import { icons } from '@config'

export default function ReportPreview({ model, fields, relationships, filters }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchPreview = async () => {
      if (!model || !fields || fields.length === 0) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const response = await API.post('api/report/preview', {
          model,
          fields,
          relationships,
          filters,
          limit: 20
        })
        setData(response)
      } catch (err) {
        console.error('Preview error:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchPreview()
  }, [model, fields, relationships, filters])

  if (loading) {
    return (
      <Card title='Förhandsgranskning' icon={icons.eye} collapsed={null}>
        <div className='text-center py-4'>
          <i className='fas fa-spinner fa-spin fa-2x'></i>
          <p className='mt-2 text-muted'>Laddar rapportdata...</p>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card title='Förhandsgranskning' icon={icons.eye} collapsed={null}>
        <div className='alert alert-danger'>
          <strong>Fel vid laddning av rapporten:</strong>
          <p className='mb-0'>{error.message || 'Ett oväntat fel uppstod'}</p>
        </div>
      </Card>
    )
  }

  if (!data) {
    return (
      <Card title='Förhandsgranskning' icon={icons.eye} collapsed={null}>
        <div className='text-center text-muted py-4'>
          <p>Ingen data att visa</p>
        </div>
      </Card>
    )
  }

  const { headers, data: rows, total, showing } = data

  // Card buttons with export functionality
  const cardButtons = {
    header: total > 0 ? [
      <ReportExport
        key="export"
        model={model}
        fields={fields}
        relationships={relationships}
        filters={filters}
      />
    ] : []
  }

  return (
    <Card
      title={`Förhandsgranskning (visar ${showing} av ${total} rader)`}
      icon={icons.eye}
      buttons={cardButtons}
      collapsed={null}
    >
      {total === 0 ? (
        <div className='text-center text-muted py-4'>
          <i className={`${icons.search} fa-2x mb-3`}></i>
          <h6>Inga resultat</h6>
          <p>Inga rader matchar de valda kriterierna.</p>
        </div>
      ) : (
        <>
          {total > showing && (
            <div className='alert alert-info mb-3'>
              <strong>Begränsad förhandsgranskning:</strong> Visar de första {showing} raderna av totalt {total} rader.
              Exportera för att få alla rader.
            </div>
          )}

          <div className='table-responsive'>
            <table className='table table-striped table-hover'>
              <thead className='table-primary'>
                <tr>
                  {Object.values(headers).map((header, index) => (
                    <th
                      key={index}
                      className='fw-bold text-nowrap'
                      style={{ minWidth: '150px', whiteSpace: 'nowrap' }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.keys(headers).map((fieldKey, colIndex) => (
                      <td key={colIndex} style={{ minWidth: '150px' }}>
                        {formatCellValue(row[fieldKey])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  )
}

function formatCellValue(value) {
  if (value === null || value === undefined) {
    return <span className='text-muted'>-</span>
  }

  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nej'
  }

  if (typeof value === 'string' && value.length > 100) {
    return (
      <span title={value}>
        {value.substring(0, 100)}...
      </span>
    )
  }

  return value
}
